<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);

const { t } = useI18n();

// A tela raiz ("Contatos") mostra UM nível; toda sub-visão (Ativos, segmento, etiqueta,
// busca) vira folha da migalha. Comparar com o próprio rótulo traduzido é o que mantém isso
// correto em qualquer idioma — nada de casar string em português.
const ehRaiz = computed(
  () => props.headerTitle === t('CONTACTS_LAYOUT.HEADER.TITLE')
);
</script>

<template>
  <!-- custom (2026-08-06): `bg-n-surface-1/80` no lugar de `bg-white/80`.
       O branco estava CRAVADO no componente — não passava por token nenhum — e por isso este
       cabeçalho continuou branco quando a plataforma inteira adotou o off-white. O mesmo
       literal existia em AppHeader e CompanyHeader; os três foram para o token.
       Fica a regra: superfície em Tailwind literal (`bg-white`) é dívida — o dia em que a
       paleta muda, ela não muda junto e o defeito só aparece na tela. -->
  <header
    class="sticky top-0 z-20 border-b border-n-weak bg-n-surface-1/90 px-3 backdrop-blur-[10px] sm:px-6"
  >
    <!-- custom (redesign Modeloja): rail do header alinhado ao contrato de largura
         (1480px), casando com o corpo da lista; título 24/600 (handoff §Header). -->
    <!-- custom (2026-08-08): o título de 24px virou MIGALHA, o padrão que já valia em
         Início, Métricas e Qualificação › Desempenho. Ele pegou a divergência na tela:
         "ao invés de aparecer 'Contatos ativos' nessa letra mais grossa, tem que aparecer
         no mesmo formato que as outras telas ali no header".
         A barra passa a ter 56px como as demais (era py-6, ~88px), e é isso que alinha o
         topo desta tela com o topo de todas as outras. -->
    <div
      class="mx-auto flex min-h-14 w-full max-w-[1480px] flex-wrap items-center justify-between gap-2 py-2 lg:flex-nowrap"
    >
      <!-- O `aria-label` sai do i18n (e não de um literal "Caminho") porque este é arquivo
           do CORE, que é traduzido de verdade — a chave da própria seção descreve a
           navegação sem inventar string nova em 30 idiomas. -->
      <nav
        class="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] leading-none"
        :aria-label="$t('CONTACTS_LAYOUT.HEADER.TITLE')"
      >
        <span
          class="shrink-0"
          :class="ehRaiz ? 'font-500 text-n-slate-12' : 'text-n-slate-10'"
        >
          {{ $t('CONTACTS_LAYOUT.HEADER.TITLE') }}
        </span>
        <template v-if="!ehRaiz">
          <span
            class="i-lucide-chevron-right size-3.5 text-n-slate-9 shrink-0"
            aria-hidden="true"
          />
          <span class="font-500 text-n-slate-12 truncate">
            {{ headerTitle }}
          </span>
        </template>
      </nav>
      <div
        class="order-last flex w-full flex-wrap items-center gap-2 lg:order-none lg:w-auto lg:flex-nowrap"
      >
        <div
          v-if="showSearch"
          class="order-last flex w-full min-w-0 items-center gap-2 lg:order-none lg:w-56"
        >
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div
          class="flex min-w-0 flex-1 items-center justify-end gap-2 overflow-x-auto lg:flex-none lg:gap-4 lg:overflow-visible"
        >
          <div class="flex shrink-0 items-center gap-2">
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                :icon="
                  isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
                "
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-amber-9"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
          </div>
          <div class="h-4 w-px shrink-0 bg-n-strong" />
          <ComposeConversation>
            <template #trigger>
              <Button :label="buttonLabel" size="sm" />
            </template>
          </ComposeConversation>
        </div>
      </div>
    </div>
  </header>
</template>
